import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useStreamingStore } from '../store/streamingStore';
import { sendStreamingMessage, buildStructuredInput } from '../api/responses';
import { messageKeys } from './useMessages';
import { StreamEventType, type StreamEvent } from '../streaming/types';
import type { ConversationMessage, MessageFile } from '@/types/conversation';
import type { PaginatedItemsResponse } from '@/types/api';
import type { InputContent } from '@/types/files';

export interface FileForOptimisticMessage {
  id: string;
  filename: string;
  mimeType: string;
  sizeBytes: number | null;
  downloadUrl?: string;
}

export function useStreamingMessage() {
  const queryClient = useQueryClient();
  const {
    status,
    textContent,
    conversationId: streamingConversationId,
    usage,
    error,
    isSearchingWeb,
    isThinking,
    reasoningSummary,
    annotations,
    processEvent,
    startStreaming,
    resetStreaming,
  } = useStreamingStore();

  const sendStreaming = useCallback(
    async (
      input: string,
      conversationId?: string,
      onConversationCreated?: (id: string) => void,
      fileIds: string[] = [],
      filesForOptimistic: FileForOptimisticMessage[] = []
    ) => {
      try {
        startStreaming(conversationId);

        // Build input (simple string or structured with files)
        const requestInput: string | InputContent[] =
          fileIds.length > 0 ? buildStructuredInput(input, fileIds) : input;

        // Add user message optimistically if we have a conversationId
        if (conversationId) {
          const queryKey = messageKeys.items(conversationId);
          queryClient.setQueryData<{
            pages: PaginatedItemsResponse[];
            pageParams: (string | undefined)[];
          }>(queryKey, (old) => {
            if (!old) return old;

            // Convert file info for optimistic display
            const optimisticFiles: MessageFile[] | undefined =
              filesForOptimistic.length > 0
                ? filesForOptimistic.map((f) => ({
                    id: f.id,
                    filename: f.filename,
                    mimeType: f.mimeType,
                    sizeBytes: f.sizeBytes,
                    downloadUrl: f.downloadUrl,
                  }))
                : undefined;

            const tempMessage: ConversationMessage = {
              id: `temp-${Date.now()}`,
              role: 'user',
              content: input,
              createdAt: new Date().toISOString(),
              files: optimisticFiles,
            };

            const newPages = [...old.pages];
            if (newPages.length > 0) {
              // Add to the START of the FIRST page (which contains newest messages)
              // Since messages are fetched in desc order and reversed for display,
              // this makes it appear at the bottom after reversal
              newPages[0] = {
                ...newPages[0],
                messages: [tempMessage, ...newPages[0].messages],
              };
            }

            return { ...old, pages: newPages };
          });
        }

        // Send message with streaming
        await sendStreamingMessage(
          requestInput,
          conversationId,
          // onEvent
          (event: StreamEvent) => {
            processEvent(event);
            // Notify when a new conversation is created
            if (
              event.type === StreamEventType.RESPONSE_CREATED &&
              !conversationId &&
              event.conversationId
            ) {
              onConversationCreated?.(event.conversationId);
            }
          },
          // onComplete
          () => {
            // Get final state from store
            const finalState = useStreamingStore.getState();
            const finalConversationId = finalState.conversationId || conversationId;
            const finalContent = finalState.textContent;

            // Optimistically add assistant message to cache before resetting
            // This prevents flicker between streaming end and refetch completion
            if (finalConversationId && finalContent) {
              const finalAnnotations = finalState.annotations;
              const queryKey = messageKeys.items(finalConversationId);
              queryClient.setQueryData<{
                pages: PaginatedItemsResponse[];
                pageParams: (string | undefined)[];
              }>(queryKey, (old) => {
                if (!old) return old;

                const assistantMessage: ConversationMessage = {
                  id: `assistant-${Date.now()}`,
                  role: 'assistant',
                  content: finalContent,
                  createdAt: new Date().toISOString(),
                  annotations: finalAnnotations.length > 0 ? finalAnnotations : undefined,
                };

                const newPages = [...old.pages];
                if (newPages.length > 0) {
                  // Add to the START of the FIRST page (which contains newest messages)
                  // Since messages are fetched in desc order and reversed for display,
                  // this makes it appear at the bottom after reversal
                  newPages[0] = {
                    ...newPages[0],
                    messages: [assistantMessage, ...newPages[0].messages],
                  };
                }

                return { ...old, pages: newPages };
              });
            }

            // Delay reset until after React renders the cache update
            // Use setTimeout to ensure React has enough time to render the cached message
            setTimeout(() => {
              resetStreaming();
            }, 100);

            // Only invalidate conversations list (for title updates)
            // Don't refetch messages - we already have the content from streaming
            queryClient.invalidateQueries({ queryKey: ['conversations'] });
          },
          // onError
          (error: Error) => {
            console.error('Streaming error:', error);
            // Keep error state visible for a moment
            setTimeout(() => resetStreaming(), 3000);
          }
        );
      } catch (error) {
        console.error('Failed to start streaming:', error);
        resetStreaming();
        throw error;
      }
    },
    [queryClient, processEvent, startStreaming, resetStreaming]
  );

  return {
    sendStreaming,
    isStreaming: status === 'streaming' || status === 'connecting',
    isConnecting: status === 'connecting',
    isCompleted: status === 'completed',
    isFailed: status === 'failed',
    streamingContent: textContent,
    streamingConversationId,
    usage,
    error,
    status,
    isSearchingWeb,
    isThinking,
    reasoningSummary,
    annotations,
    resetStreaming,
  };
}
